import React from 'react';
import { format, parseISO } from 'date-fns';
import { BookingResponse, LocationType } from '@/client/types.gen';
import {
  CalendarCheck,
  Clock,
  MapPin,
  User,
  Mail,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface BookingLocation {
  type: LocationType;
  details?: Record<string, unknown> | null;
}

interface GoogleMeetDetails {
  conference_id?: string;
  join_url?: string;
  more_options?: {
    url?: string;
    phone_numbers?: Array<{
      url?: string;
      label?: string;
      pin?: string;
      region_code?: string;
    }>;
  };
}

// Extend the BookingResponse type to include uid
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
}

interface BookingConfirmationProps {
  booking: ExtendedBookingResponse;
  onDone: () => void;
}

export function BookingConfirmation({
  booking,
  onDone,
}: BookingConfirmationProps) {
  // Format date and time
  const startTime = parseISO(booking.start_time);
  const endTime = parseISO(booking.end_time);

  const formatDate = (date: Date) => format(date, 'EEEE, MMMM d, yyyy');
  const formatTime = (date: Date) => format(date, 'h:mm a');

  // Get primary invitee
  const primaryInvitee = booking.invitees.find(
    (inv) => inv.is_primary
  )?.invitee_info;

  // Format location type
  const formatLocationType = (type: LocationType): string => {
    return type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Handle location details display based on location type
  const renderLocationDetails = (
    locationType: LocationType,
    details: Record<string, unknown> | null
  ) => {
    if (!details) return null;

    if (locationType === 'google_meet') {
      return renderGoogleMeetDetails(details as unknown as GoogleMeetDetails);
    }

    // Fallback for other location types
    return (
      <p className="text-sm text-muted-foreground">{JSON.stringify(details)}</p>
    );
  };

  // Render Google Meet specific details
  const renderGoogleMeetDetails = (details: GoogleMeetDetails) => {
    return (
      <div className="mt-2 space-y-2">
        {details.join_url && (
          <div>
            <a
              href={details.join_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary flex items-center hover:underline"
            >
              <span>Join Google Meet</span>
              <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </div>
        )}

        {details.more_options?.phone_numbers &&
          details.more_options.phone_numbers.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mt-2">Dial-in options:</p>
              {details.more_options.phone_numbers.map((phone, idx) => (
                <p key={idx}>
                  {phone.label}: {phone.url?.replace('tel:', '')}
                  {phone.pin && <span> PIN: {phone.pin}</span>}
                </p>
              ))}
            </div>
          )}
      </div>
    );
  };

  // Check if location is a valid location object
  const isValidLocation = (location: unknown): location is BookingLocation => {
    return (
      !!location &&
      typeof location === 'object' &&
      'type' in location &&
      typeof location.type === 'string'
    );
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader className="bg-muted border-b">
        <div className="flex items-center justify-center mb-4">
          <CalendarCheck className="h-12 w-12 text-primary" />
        </div>
        <CardTitle className="text-xl text-center">
          Booking Confirmed!
        </CardTitle>
        <CardDescription className="text-center">
          Your meeting has been scheduled successfully.
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-6 space-y-4">
        <h3 className="text-lg font-medium">{booking.title}</h3>

        {booking.description && (
          <p className="text-sm text-muted-foreground">{booking.description}</p>
        )}

        <div className="space-y-3 mt-4">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium">{formatDate(startTime)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium">
                {formatTime(startTime)} - {formatTime(endTime)}
              </p>
            </div>
          </div>

          {primaryInvitee && (
            <>
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{primaryInvitee.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{primaryInvitee.email}</p>
                </div>
              </div>
            </>
          )}

          {booking.location && isValidLocation(booking.location) && (
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">
                  {formatLocationType(booking.location.type)}
                </p>
                {booking.location.details &&
                  renderLocationDetails(
                    booking.location.type,
                    booking.location.details
                  )}
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="border-t pt-6 flex justify-center bg-muted/20">
        <Button onClick={onDone}>Done</Button>
      </CardFooter>
    </Card>
  );
}
